<template>
  <div class="p-4 sm:p-6 lg:p-8">
    <div class="mb-6">
      <h1 class="text-2xl font-semibold leading-6 text-gray-900 dark:text-gray-100">File Processing Preview</h1>
      <p class="mt-2 text-sm text-gray-700 dark:text-gray-300">
        Upload a PDF or DOCX to view extracted markdown and chunk output.
      </p>
    </div>

    <div class="bg-base-100 rounded-lg shadow p-4">
      <div class="mb-4">
        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">File</label>
        <input type="file" accept=".pdf,.docx" class="file-input file-input-bordered w-full" @change="onFileChange" />
        <div v-if="selectedFile !== null" class="text-sm text-gray-600 dark:text-gray-300 mt-2">
          <span class="font-medium">Selected:</span> {{ selectedFile.name }} ({{ formatBytes(selectedFile.size) }})
        </div>
      </div>

      <div class="flex items-center flex-wrap">
        <button class="btn btn-sm" @click="runPreview" :disabled="canRun === false">
          <span v-if="isLoading === true" class="loading loading-spinner loading-xs mr-2"></span>
          Preview File Processing
        </button>
        <button v-if="selectedFile !== null" class="btn btn-ghost btn-sm ml-2" @click="clearSelection">Clear</button>
      </div>

      <div v-if="error !== null" class="alert alert-error mt-4">
        <div><span>{{ error }}</span></div>
      </div>
    </div>

    <div v-if="result !== null" class="mt-6">
      <div class="bg-base-100 rounded-lg shadow p-4">
        <div class="tabs tabs-boxed mb-4">
          <button class="tab" :class="{ 'tab-active': activeTab === 'markdown' }" @click="activeTab = 'markdown'">Markdown</button>
          <button class="tab" :class="{ 'tab-active': activeTab === 'chunks' }" @click="activeTab = 'chunks'">Chunks</button>
        </div>

        <div v-if="activeTab === 'markdown'">
          <div class="text-sm text-gray-700 dark:text-gray-300 mb-2">
            <span class="font-medium">File:</span> {{ result.fileName }}
          </div>
          <div v-if="renderedMarkdown.length > 0" class="w-full border border-base-300 rounded-lg p-4 max-h-[70vh] overflow-y-auto bg-base-200">
            <div class="prose prose-sm dark:prose-invert max-w-none" v-html="renderedMarkdown"></div>
          </div>
          <div v-else class="text-sm text-gray-700 dark:text-gray-300">
            No markdown output available.
          </div>
        </div>

        <div v-if="activeTab === 'chunks'">
          <div class="overflow-x-auto">
            <table class="table table-zebra table-sm w-full">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Chars</th>
                  <th>Chunk Text</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="chunk in chunkRows" :key="chunk.id">
                  <td class="align-top min-w-[120px]">{{ chunk.id }}</td>
                  <td class="align-top min-w-[100px]">{{ chunk.charCount }}</td>
                  <td class="align-top whitespace-pre-wrap break-words">{{ chunk.pageContent }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-if="chunkRows.length === 0" class="text-sm text-gray-700 dark:text-gray-300 mt-2">
            No chunks available.
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import axios from '../../api/axiosInstance';
import { marked } from 'marked';
import { sanitizeHtml } from '@/shared/utils/sanitizeHtml';

const selectedFile = ref(null);
const error = ref(null);
const isLoading = ref(false);
const result = ref(null);
const activeTab = ref('markdown');

const canRun = computed(() => selectedFile.value !== null && isLoading.value === false);
const markdownSource = computed(() => {
  if (result.value === null) {
    return '';
  }
  const markdownPreview = typeof result.value.markdownPreview === 'string' ? result.value.markdownPreview : '';
  if (markdownPreview.length > 0) {
    return markdownPreview;
  }
  const markdown = typeof result.value.markdown === 'string' ? result.value.markdown : '';
  if (markdown.length > 0) {
    return markdown;
  }
  const preSplit = result.value.preSplit !== null && result.value.preSplit !== undefined ? result.value.preSplit : null;
  if (preSplit === null) {
    return '';
  }
  const pages = Array.isArray(preSplit.pages) === true ? preSplit.pages : [];
  if (pages.length > 0) {
    return pages.map((page, index) => {
      const title = `## Page ${index + 1}`;
      const content = typeof page?.previewText === 'string' ? page.previewText : '';
      if (content.length === 0) {
        return title;
      }
      return `${title}\n\n${content}`;
    }).join('\n\n');
  }
  const previewText = typeof preSplit.previewText === 'string' ? preSplit.previewText : '';
  if (previewText.length > 0) {
    return previewText;
  }
  return '';
});
const renderedMarkdown = computed(() => {
  const markdown = markdownSource.value;
  if (markdown.length === 0) {
    return '';
  }
  return sanitizeHtml(marked.parse(markdown));
});
const chunkRows = computed(() => {
  if (result.value === null) {
    return [];
  }
  const chunks = Array.isArray(result.value.chunks) === true ? result.value.chunks : [];
  if (chunks.length > 0) {
    return chunks.map((chunk, index) => {
      const chunkId = typeof chunk?.id === 'string' && chunk.id.length > 0 ? chunk.id : `chunk-${index + 1}`;
      const chunkText = typeof chunk?.pageContent === 'string' ? chunk.pageContent : '';
      const charCount = Number.isFinite(Number(chunk?.charCount)) === true ? Number(chunk.charCount) : chunkText.length;
      return {
        id: chunkId,
        pageContent: chunkText,
        charCount
      };
    });
  }
  const preSplit = result.value.preSplit !== null && result.value.preSplit !== undefined ? result.value.preSplit : null;
  if (preSplit === null) {
    return [];
  }
  const pages = Array.isArray(preSplit.pages) === true ? preSplit.pages : [];
  if (pages.length > 0) {
    return pages.map((page, index) => {
      const chunkText = typeof page?.previewText === 'string' ? page.previewText : '';
      const charCount = Number.isFinite(Number(page?.textLength)) === true ? Number(page.textLength) : chunkText.length;
      return {
        id: `page-${index + 1}`,
        pageContent: chunkText,
        charCount
      };
    });
  }
  const previewText = typeof preSplit.previewText === 'string' ? preSplit.previewText : '';
  if (previewText.length === 0) {
    return [];
  }
  const charCount = Number.isFinite(Number(preSplit.textLength)) === true ? Number(preSplit.textLength) : previewText.length;
  return [{
    id: 'doc-1',
    pageContent: previewText,
    charCount
  }];
});

function getFileType(fileName) {
  const name = typeof fileName === 'string' ? fileName.trim() : '';
  const dotIndex = name.lastIndexOf('.');
  if (dotIndex >= 0 && dotIndex < name.length - 1) {
    return name.slice(dotIndex + 1).toLowerCase();
  }
  return '';
}

function formatBytes(value) {
  const size = Number(value);
  if (Number.isFinite(size) === false) {
    return '';
  }
  if (size === 0) {
    return '0 B';
  }
  const k = 1024;
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const index = Math.floor(Math.log(size) / Math.log(k));
  const formatted = size / Math.pow(k, index);
  const decimals = index === 0 ? 0 : 2;
  return `${formatted.toFixed(decimals)} ${units[index]}`;
}

function clearSelection() {
  selectedFile.value = null;
  error.value = null;
  result.value = null;
}

function onFileChange(event) {
  const input = event.target;
  const files = input !== null && input !== undefined ? input.files : null;
  const file = files !== null && files.length > 0 ? files[0] : null;
  if (file === null) {
    selectedFile.value = null;
    return;
  }
  const fileType = getFileType(file.name);
  if (fileType !== 'pdf' && fileType !== 'docx') {
    error.value = 'Only pdf and docx files are supported';
    selectedFile.value = null;
    result.value = null;
    if (input !== null && input !== undefined) {
      input.value = '';
    }
    return;
  }
  selectedFile.value = file;
  error.value = null;
  result.value = null;
}

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const data = typeof reader.result === 'string' ? reader.result : '';
      if (data.length > 0) {
        resolve(data);
      } else {
        reject(new Error('Failed to read file'));
      }
    };
    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };
    reader.readAsDataURL(file);
  });
}

async function runPreview() {
  if (selectedFile.value === null) {
    error.value = 'Please select a file';
    return;
  }
  isLoading.value = true;
  error.value = null;
  result.value = null;
  activeTab.value = 'markdown';
  try {
    const base64 = await fileToBase64(selectedFile.value);
    const fileType = getFileType(selectedFile.value.name);
    const response = await axios.post('/qa/fileProcessingPreview', {
      fileName: selectedFile.value.name,
      fileType,
      fileData: base64
    });
    result.value = response.data;
  } catch (err) {
    const responseError = err !== null && err !== undefined ? err.response?.data?.error : null;
    if (typeof responseError === 'string' && responseError.length > 0) {
      error.value = responseError;
    } else {
      error.value = 'Failed to preview file processing';
    }
  } finally {
    isLoading.value = false;
  }
}
</script>
